import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const WIDTH = 800;
const HEIGHT = 400;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 60 };

const METHOD_COLORS = {
  linear: '#2563eb',
  ema: '#16a34a',
  meanReversion: '#f59e0b',
  randomWalk: '#9ca3af',
};

const METHOD_LABELS = {
  linear: 'Linear Trend',
  ema: 'EMA',
  meanReversion: 'Mean Reversion',
  randomWalk: 'Random Walk',
};

const ENSEMBLE_COLOR = '#7c3aed';
const HISTORICAL_COLOR = '#1f2937';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  select: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    color: '#1f2937',
    cursor: 'pointer',
    outline: 'none',
  },
  selectLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: 500,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function EnsembleForecastChart({ data }) {
  const svgRef = useRef(null);
  const [selectedField, setSelectedField] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Set initial selected field when data arrives
  useEffect(() => {
    if (data && data.fields && data.fields.length > 0 && !selectedField) {
      setSelectedField(data.fields[0].field);
    }
  }, [data, selectedField]);

  useEffect(() => {
    if (!data || !data.fields || data.fields.length === 0 || !selectedField) return;

    const fieldData = data.fields.find((f) => f.field === selectedField);
    if (!fieldData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const historical = fieldData.historical || [];
    const methods = fieldData.methods || {};
    const ensemble = fieldData.ensemble || [];

    // Determine the number of forecast steps
    const forecastSteps = ensemble.length;

    // Build combined x-axis: historical dates + forecast steps
    const totalPoints = historical.length + forecastSteps;
    const x = d3.scaleLinear().domain([0, totalPoints - 1]).range([0, innerW]);

    // Collect all y values for the domain
    const allValues = [];
    historical.forEach((d) => allValues.push(d.value));
    Object.values(methods).forEach((series) => {
      series.forEach((d) => allValues.push(d.value));
    });
    ensemble.forEach((d) => {
      allValues.push(d.value);
      if (d.spread != null) {
        allValues.push(d.value + d.spread);
        allValues.push(d.value - d.spread);
      }
    });

    const yMin = d3.min(allValues);
    const yMax = d3.max(allValues);
    const yPadding = (yMax - yMin) * 0.1 || 1;

    const y = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis with mixed labels: dates for historical, "Step N" for forecast
    const xTickIndices = [];
    const maxTicks = 12;
    const step = Math.max(1, Math.floor(totalPoints / maxTicks));
    for (let i = 0; i < totalPoints; i += step) {
      xTickIndices.push(i);
    }
    if (xTickIndices[xTickIndices.length - 1] !== totalPoints - 1) {
      xTickIndices.push(totalPoints - 1);
    }

    const xAxis = g.append('g').attr('transform', `translate(0, ${innerH})`);

    xAxis
      .call(
        d3.axisBottom(x)
          .tickValues(xTickIndices)
          .tickSize(0)
          .tickFormat((i) => {
            const idx = Math.round(i);
            if (idx < historical.length) {
              return formatDateLabel(historical[idx].date);
            }
            const forecastIdx = idx - historical.length;
            return `Step ${forecastIdx + 1}`;
          })
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    xAxis.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Boundary line between historical and forecast
    if (historical.length > 0 && forecastSteps > 0) {
      const boundaryX = x(historical.length - 0.5);
      g.append('line')
        .attr('x1', boundaryX)
        .attr('x2', boundaryX)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3');
    }

    // Historical line
    if (historical.length > 1) {
      const historicalLine = d3.line()
        .x((d, i) => x(i))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(historical)
        .attr('d', historicalLine)
        .attr('fill', 'none')
        .attr('stroke', HISTORICAL_COLOR)
        .attr('stroke-width', 2);

      g.selectAll('.hist-dot')
        .data(historical)
        .enter()
        .append('circle')
        .attr('cx', (d, i) => x(i))
        .attr('cy', (d) => y(d.value))
        .attr('r', 3)
        .attr('fill', HISTORICAL_COLOR);
    }

    // Ensemble spread band
    if (ensemble.length > 0 && ensemble.some((d) => d.spread != null)) {
      const spreadArea = d3.area()
        .x((d) => x(historical.length + d.step - 1))
        .y0((d) => y(d.value - (d.spread || 0)))
        .y1((d) => y(d.value + (d.spread || 0)))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(ensemble)
        .attr('d', spreadArea)
        .attr('fill', ENSEMBLE_COLOR)
        .attr('fill-opacity', 0.1)
        .attr('stroke', 'none');
    }

    // Individual forecast method lines
    const methodNames = Object.keys(methods);
    methodNames.forEach((methodName) => {
      const series = methods[methodName];
      if (!series || series.length === 0) return;

      const methodLine = d3.line()
        .x((d) => x(historical.length + d.step - 1))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(series)
        .attr('d', methodLine)
        .attr('fill', 'none')
        .attr('stroke', METHOD_COLORS[methodName] || '#6b7280')
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', 0.7);
    });

    // Ensemble forecast line (thick dashed)
    if (ensemble.length > 0) {
      const ensembleLine = d3.line()
        .x((d) => x(historical.length + d.step - 1))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(ensemble)
        .attr('d', ensembleLine)
        .attr('fill', 'none')
        .attr('stroke', ENSEMBLE_COLOR)
        .attr('stroke-width', 3)
        .attr('stroke-dasharray', '8,4');
    }

    // Invisible hover targets for historical points
    g.selectAll('.hist-hover')
      .data(historical)
      .enter()
      .append('circle')
      .attr('cx', (d, i) => x(i))
      .attr('cy', (d) => y(d.value))
      .attr('r', 10)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          type: 'historical',
          date: d.date,
          value: d.value,
        });
      })
      .on('mouseleave', () => setTooltip(null));

    // Invisible hover targets for ensemble forecast points
    if (ensemble.length > 0) {
      g.selectAll('.ensemble-hover')
        .data(ensemble)
        .enter()
        .append('circle')
        .attr('cx', (d) => x(historical.length + d.step - 1))
        .attr('cy', (d) => y(d.value))
        .attr('r', 10)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event, d) => {
          const methodValues = {};
          methodNames.forEach((m) => {
            const pt = methods[m].find((p) => p.step === d.step);
            if (pt) methodValues[m] = pt.value;
          });
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'forecast',
            step: d.step,
            value: d.value,
            spread: d.spread,
            methodValues,
          });
        })
        .on('mouseleave', () => setTooltip(null));
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, selectedField]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Ensemble Forecast</div>
        <div style={styles.subtitle}>Combined linear trend, EMA, mean reversion, and random walk forecasts</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No ensemble forecast data available
        </div>
      </div>
    );
  }

  const fieldData = data.fields.find((f) => f.field === selectedField);
  const weights = fieldData ? fieldData.weights || {} : {};

  return (
    <div style={styles.card}>
      <div style={styles.title}>Ensemble Forecast</div>
      <div style={styles.subtitle}>Combined linear trend, EMA, mean reversion, and random walk forecasts</div>

      {/* Field selector */}
      <div style={styles.controls}>
        <span style={styles.selectLabel}>Price field:</span>
        <select
          style={styles.select}
          value={selectedField || ''}
          onChange={(e) => setSelectedField(e.target.value)}
        >
          {data.fields.map((f) => (
            <option key={f.field} value={f.field}>
              {f.field}
            </option>
          ))}
        </select>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={HISTORICAL_COLOR} strokeWidth="2" />
          </svg>
          <span>Historical</span>
        </div>
        {Object.keys(METHOD_COLORS).map((method) => (
          <div key={method} style={styles.legendItem}>
            <svg width="20" height="10">
              <line
                x1="0" y1="5" x2="20" y2="5"
                stroke={METHOD_COLORS[method]}
                strokeWidth="1.5"
                strokeOpacity="0.7"
              />
            </svg>
            <span>
              {METHOD_LABELS[method] || method}
              {weights[method] != null ? ` (${(weights[method] * 100).toFixed(0)}%)` : ''}
            </span>
          </div>
        ))}
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line
              x1="0" y1="5" x2="20" y2="5"
              stroke={ENSEMBLE_COLOR}
              strokeWidth="3"
              strokeDasharray="4,2"
            />
          </svg>
          <span>Ensemble</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill={ENSEMBLE_COLOR} fillOpacity="0.1" rx="2" />
          </svg>
          <span>Spread Band</span>
        </div>
      </div>

      <div style={styles.container}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.type === 'historical' ? (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                {formatDateFull(tooltip.date)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Price</span>
                <span style={{ fontWeight: 600, color: HISTORICAL_COLOR }}>
                  ${tooltip.value.toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                Step {tooltip.step}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Ensemble</span>
                <span style={{ fontWeight: 600, color: ENSEMBLE_COLOR }}>
                  ${tooltip.value.toFixed(2)}
                </span>
              </div>
              {tooltip.spread != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                  <span style={{ color: '#6b7280' }}>Spread</span>
                  <span style={{ fontWeight: 600 }}>
                    &plusmn;${tooltip.spread.toFixed(2)}
                  </span>
                </div>
              )}
              {tooltip.methodValues && Object.keys(tooltip.methodValues).map((m) => (
                <div
                  key={m}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '1px' }}
                >
                  <span style={{ color: METHOD_COLORS[m] || '#6b7280' }}>
                    {METHOD_LABELS[m] || m}
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    ${tooltip.methodValues[m].toFixed(2)}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
